// =============================================================================
// FILE: packages/renderer/src/hooks/useKeyboardShortcuts.ts
// PURPOSE: Global keyboard shortcuts for the desktop UX layer. Strictly
//          renderer-side — no IPC, no main-process menus. The handlers are
//          passed in by App.tsx so the same hook keeps the routing surface
//          untouched.
//
//          Shortcut map (Ctrl on Windows/Linux, ⌘ on macOS):
//            Ctrl/⌘ + P              → navigate to Projects
//            Ctrl/⌘ + ,              → navigate to Settings
//            Ctrl/⌘ + K              → focus global search (TopBar input)
//            Ctrl/⌘ + S              → fire app-wide "save" event for the
//                                       currently focused page to opt into
//
//          Inputs and contenteditable surfaces are skipped so the user can
//          still type literal characters into search boxes.
// =============================================================================

import { useEffect } from 'react';

export const APP_SAVE_EVENT = 'bim-ops:save-shortcut';

export interface KeyboardShortcutHandlers {
  onProjects: () => void;
  onSettings: () => void;
  onSearch:   () => void;
}

export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const editing =
        tag === 'input' ||
        tag === 'textarea' ||
        (target?.isContentEditable ?? false);

      // Ctrl/⌘ + K is allowed even inside inputs because the global search
      // is the explicit intent. Everything else respects "user is typing".
      const key = e.key.toLowerCase();

      if (key === 'k') {
        e.preventDefault();
        handlers.onSearch();
        return;
      }

      if (editing) return;

      if (key === 'p') {
        e.preventDefault();
        handlers.onProjects();
        return;
      }

      // The comma key is the canonical "Preferences" shortcut on every
      // major desktop OS — VS Code, Chrome, Firefox, macOS apps.
      if (e.key === ',') {
        e.preventDefault();
        handlers.onSettings();
        return;
      }

      if (key === 's') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(APP_SAVE_EVENT));
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handlers]);
}
